import React, { useEffect, useMemo, useRef, useState } from "react";
import { PortableText } from "@portabletext/react";
import {
  ArrowRight,
  Pause,
  Play,
  ShieldCheck,
  Route,
  Package,
  CloudSun,
  Mail,
  Download,
  ChevronLeft,
  ChevronRight,
  Wrench,
  Linkedin,
  Facebook,
  Twitter,
  ExternalLink,
  Menu,
  X,
} from "lucide-react";
import {
  brand as mockBrand,
  heroMedia as mockHeroMedia,
  products as mockProducts,
  features as mockFeatures,
  useCases as mockUseCases,
  team as mockTeam,
  partners as mockPartners,
  blogPosts as mockBlogPosts,
  faqs as mockFaqs,
} from "../mocks/mock";
import { fetchSanity, sanityClient } from "../lib/sanity";

import { Button } from "../components/ui/button.jsx";
import { Card, CardContent } from "../components/ui/card.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog.jsx";
import { useToast } from "../hooks/use-toast";

import { createPortableTextComponents } from "./portableTextSerializers";
import LanguageSwitcher from "./LanguageSwitcher";
import "./LandingPage.css";
import { useI18n } from "../i18n/I18nContext";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  EmailShareButton,
} from "react-share";

const icons = { ShieldCheck, Route, Package, CloudSun, Wrench };

//const SHOULD_USE_MOCK_DATA = process.env.NODE_ENV !== "production";
const SHOULD_USE_MOCK_DATA = false;

const CONTACT_ENDPOINT = (() => {
  const rawBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").trim();
  if (!rawBaseUrl) {
    return "/api/contact";
  }

  const normalizedBaseUrl = rawBaseUrl.replace(/\/$/, "");

  if (typeof window !== "undefined") {
    try {
      const apiUrl = new URL(normalizedBaseUrl);
      const apiHost = apiUrl.hostname.toLowerCase();
      const currentHost = window.location.hostname.toLowerCase();
      const isLocalApi = apiHost === "localhost" || apiHost === "127.0.0.1";
      const isLocalPage = currentHost === "localhost" || currentHost === "127.0.0.1";

      if (isLocalApi && !isLocalPage) {
        return "/api/contact";
      }

      return `${normalizedBaseUrl}/api/contact`;
    } catch (error) {
      console.warn("Invalid REACT_APP_API_BASE_URL", error);
      return "/api/contact";
    }
  }

  return `${normalizedBaseUrl}/api/contact`;
})();

const DEFAULT_ACCENT = "#C2188F";
const DEFAULT_ACCENT_ALT = "#7E2D91";
const VIDEO_URL_PATTERN = /\.(mp4|webm|ogg|mov|m4v)(?:[?#].*)?$/i;
const IMAGE_URL_PATTERN = /\.(avif|gif|jpe?g|png|svg|webp|bmp|tiff?)(?:[?#].*)?$/i;

const buildSanityAssetUrl = (ref, { width, height, fit, auto = "format" } = {}) => {
  if (!ref || !sanityClient) return "";
  const config = sanityClient.config?.();
  if (!config?.projectId || !config?.dataset) return "";

  const segments = ref.split("-");
  if (segments.length < 2) return "";

  const type = segments[0];
  const format = segments[segments.length - 1];
  const assetId = segments.slice(1, segments.length - 1).join("-");
  const bucket = type === "file" ? "files" : "images";
  const baseUrl = `https://cdn.sanity.io/${bucket}/${config.projectId}/${config.dataset}/${assetId}.${format}`;

  if (type !== "image") {
    return baseUrl;
  }

  const params = new URLSearchParams();
  if (width) params.set("w", width);
  if (height) params.set("h", height);
  if (fit) params.set("fit", fit);
  if (auto) params.set("auto", auto);
  const query = params.toString();
  return query ? `${baseUrl}?${query}` : `${baseUrl}?auto=format`;
};

const getMediaUrl = (media, options) => {
  if (!media) return "";
  if (typeof media === "string") return media;
  if (Array.isArray(media)) return getMediaUrl(media[0], options);
  if (media.url) return media.url;
  if (media.src) return media.src;
  if (media.asset?.url) return media.asset.url;
  if (media._ref) return buildSanityAssetUrl(media._ref, options);
  if (media.asset?._ref) return buildSanityAssetUrl(media.asset._ref, options);
  if (media.image) return getMediaUrl(media.image, options);
  if (media.media) return getMediaUrl(media.media, options);
  if (media.file) return getMediaUrl(media.file, options);
  return "";
};

const getImageUrl = (image, options) => getMediaUrl(image, options);
const isVideoUrl = (url) => VIDEO_URL_PATTERN.test((url || "").trim());
const isImageUrl = (url) => IMAGE_URL_PATTERN.test((url || "").trim());
const firstNonEmptyUrl = (...candidates) => candidates.find((value) => typeof value === "string" && value.trim()) || "";

const getHeroVideoUrl = (heroMedia) => firstNonEmptyUrl(
  getMediaUrl(heroMedia?.video),
  heroMedia?.videoUrl,
  isVideoUrl(heroMedia?.url) ? heroMedia.url : "",
  heroMedia?.media?.type === "video" ? getMediaUrl(heroMedia?.media) : "",
  heroMedia?.type === "video" ? getMediaUrl(heroMedia?.media) : "",
);

const getHeroImageUrl = (heroMedia, allowGenericUrl = true) => firstNonEmptyUrl(
  getImageUrl(heroMedia?.background),
  getImageUrl(heroMedia?.image),
  heroMedia?.media?.type === "image" ? getImageUrl(heroMedia?.media) : "",
  allowGenericUrl && isImageUrl(heroMedia?.url) ? heroMedia.url : "",
  allowGenericUrl && heroMedia?.type !== "video" && heroMedia?.media?.type !== "video"
    ? getMediaUrl(heroMedia?.media)
    : "",
);

const hexToRgb = (hex) => {
  if (!hex) return null;
  const normalized = hex.replace("#", "");
  if (normalized.length === 3) {
    const r = parseInt(normalized[0] + normalized[0], 16);
    const g = parseInt(normalized[1] + normalized[1], 16);
    const b = parseInt(normalized[2] + normalized[2], 16);
    return [r, g, b];
  }
  if (normalized.length === 6) {
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return [r, g, b];
  }
  return null;
};

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

const applyBrandTheme = (brand) => {
  if (typeof document === "undefined") return;
  const accent = brand?.accent || DEFAULT_ACCENT;
  const accentAlt = brand?.accentAlt || accent;
  const root = document.documentElement;
  const accentRgb = hexToRgb(accent);
  const accentAltRgb = hexToRgb(accentAlt);

  root.style.setProperty("--brand-accent", accent);
  root.style.setProperty("--brand-accent-alt", accentAlt);
  if (accentRgb) {
    root.style.setProperty("--brand-accent-rgb", accentRgb.join(", "));
  }
  if (accentAltRgb) {
    root.style.setProperty("--brand-accent-alt-rgb", accentAltRgb.join(", "));
  }
};

const Whatsapp = (props) => (
  <svg
    {...props}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M20.52 3.5A11.8 11.8 0 0012 0 12 12 0 000 12a11.8 11.8 0 0017.37 10.38l2.63.83-.84-2.61A11.8 11.8 0 0024 12a11.8 11.8 0 00-3.48-8.5zM12 22a10 10 0 01-5-1.33L3 22l1.3-4.01A9.9 9.9 0 012 12a10 10 0 0110-10 9.8 9.8 0 017 3A10 10 0 0122 12a9.9 9.9 0 01-2.86 7.06 9.8 9.8 0 01-7.14 2.94z" />
    <path d="M17.57 14.1c-.3-.15-1.76-.86-2.03-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.48-1.76-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37-.27.3-1.03 1-1.03 2.45s1.05 2.84 1.2 3.03c.15.2 2.07 3.16 5.02 4.43.7.3 1.25.48 1.67.62.7.22 1.34.19 1.85.11.56-.08 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

const Header = ({ brand }) => {
  const { t } = useI18n();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const dismiss = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", dismiss);
    return () => window.removeEventListener("keydown", dismiss);
  }, [isMenuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleScroll = () => setIsScrolled(window.scrollY > 10);

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const nav = [
    { label: "Products", href: "#products" },
    { label: "Technology", href: "#features" },
    { label: "Use Cases", href: "#use-cases" },
    { label: "Team", href: "#team" },
    { label: "Partners", href: "#partners" },
    { label: "Blogs", href: "#blog" },
  ];

  const logoSrc = getImageUrl(brand?.logo) || brand?.logoUrl || "";
  const containerClasses = [
    "site-header__inner relative mx-auto grid w-full max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-4 px-6 py-5 text-white",
    isScrolled ? "is-scrolled" : "",
  ].join(" ");

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className={`site-header fixed inset-x-0 top-0 z-50 ${isScrolled ? "is-scrolled" : ""}`}>
      <div className={containerClasses}>
        <a href="#top" className="flex shrink-0 items-center gap-3 text-white" aria-label={`${brand?.name || "IMAGO"} — ${t("Home")}`}>
          {logoSrc ? (
            <img src={logoSrc} alt="IMAGO logo" className="h-8 w-auto max-w-[150px] object-contain" />
          ) : (
            <span className="text-sm font-semibold uppercase tracking-[0.35em]">
              {brand?.name || "IMAGO"}
            </span>
          )}
        </a>
        <nav aria-label={t("Main navigation")} className="hidden min-w-0 items-center justify-center gap-6 min-[1320px]:flex">
          {nav.map((n) => (
            <a
              key={n.label}
              href={n.href}
              className="whitespace-nowrap text-xs font-medium text-white/80 transition hover:text-white"
            >
              {t(n.label)}
            </a>
          ))}
        </nav>
        <div className="col-start-3 flex shrink-0 items-center justify-end gap-3">
          <LanguageSwitcher />
          <Button asChild className="site-button-primary hidden sm:inline-flex">
            <a href="#contact">{t("Contact sales")}</a>
          </Button>
          <button
            type="button"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-white/25 text-white transition hover:bg-white/10 min-[1320px]:hidden"
            aria-label={t(isMenuOpen ? "Close navigation" : "Open navigation")}
            aria-expanded={isMenuOpen}
            ref={menuButtonRef}
            aria-controls="main-navigation-menu"
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        <nav
          id="main-navigation-menu"
          className={`absolute inset-x-0 top-full overflow-hidden border-t border-white/15 bg-[#0a0b10] px-4 py-3 min-[1320px]:hidden ${isMenuOpen ? "grid" : "hidden"}`}
          aria-label={t("Main navigation")}
        >
          {nav.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={closeMenu}
              className="rounded-md px-3 py-3 text-sm text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              {t(item.label)}
            </a>
          ))}
          <a href="#contact" onClick={closeMenu} className="rounded-md px-3 py-3 text-sm font-semibold text-white transition hover:bg-white/10">
            {t("Contact sales")}
          </a>
        </nav>
      </div>
    </header>
  );
};

const Hero = ({ heroMedia }) => {
  const { t, field } = useI18n();
  const heroRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const videoRef = useRef(null);
  const [isVideoPaused, setIsVideoPaused] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(preference.matches);
    preference.addEventListener("change", update);
    return () => preference.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (reduceMotion) videoRef.current?.pause();
  }, [reduceMotion]);
  const primaryCta = heroMedia?.primaryCta || {};
  const secondaryCta = heroMedia?.secondaryCta || {};
  const primaryCtaLabel = field(heroMedia, "primaryCtaLabel") || field(primaryCta, "label");
  const secondaryCtaLabel = field(heroMedia, "secondaryCtaLabel") || field(secondaryCta, "label");
  const videoUrl = getHeroVideoUrl(heroMedia);
  const backgroundUrl = getHeroImageUrl(heroMedia, !videoUrl);
  const posterUrl = firstNonEmptyUrl(
    getImageUrl(heroMedia?.poster),
    getImageUrl(heroMedia?.imagePoster),
    backgroundUrl,
  );
  const mediaType = videoUrl ? "video" : "image";
  const mediaScale = 1.08 - scrollProgress * 0.08;
  const mediaTranslateY = scrollProgress * 28;
  const contentTranslateY = -scrollProgress * 12;

  useEffect(() => {
    if (typeof window === "undefined" || reduceMotion) return undefined;

    let frameId = null;

    const updateProgress = () => {
      frameId = null;
      const heroElement = heroRef.current;
      if (!heroElement) return;

      const { top, height } = heroElement.getBoundingClientRect();
      const scrollableDistance = Math.max(height - window.innerHeight, 1);
      const nextProgress = clamp(-top / scrollableDistance);

      setScrollProgress((current) => (
        Math.abs(current - nextProgress) > 0.002 ? nextProgress : current
      ));
    };

    const requestMeasure = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateProgress);
    };

    updateProgress();
    window.addEventListener("scroll", requestMeasure, { passive: true });
    window.addEventListener("resize", requestMeasure);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener("scroll", requestMeasure);
      window.removeEventListener("resize", requestMeasure);
    };
  }, [reduceMotion]);

  if (!heroMedia) return null;

  return (
    <section
      ref={heroRef}
      id="top"
      className="site-hero relative isolate bg-[#05060d] text-white"
    >
      <div className="site-hero__stage sticky top-0 overflow-hidden">
        {mediaType === "video" && videoUrl ? (
          <video
            ref={videoRef}
            className="site-hero__media absolute inset-0 h-full w-full object-cover"
            src={videoUrl}
            onPlay={() => setIsVideoPaused(false)}
            onPause={() => setIsVideoPaused(true)}
            aria-hidden="true"
            autoPlay={!reduceMotion}
            loop
            muted
            playsInline
            poster={posterUrl || undefined}
            style={{ transform: `translate3d(0, ${mediaTranslateY}px, 0) scale(${mediaScale})` }}
          />
        ) : (
          backgroundUrl ? (
            <img
              src={backgroundUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              fetchPriority="high"
              style={{ transform: `translate3d(0, ${mediaTranslateY}px, 0) scale(${mediaScale})` }}
            />
          ) : null
        )}

        <div className="site-hero__shade absolute inset-0" aria-hidden="true" />

        <div className="site-hero__layout relative z-10 mx-auto flex h-full w-full max-w-7xl items-end px-6">
          <div
            className="site-hero__content max-w-3xl"
            style={{
              transform: `translate3d(0, ${contentTranslateY}px, 0)`,
            }}
          >
            {heroMedia?.headline ? (
              <h1 className="font-heading text-4xl font-medium leading-tight md:text-6xl">
                {field(heroMedia, "headline")}
              </h1>
            ) : null}
            <div className="site-hero__summary">
              {heroMedia?.subhead ? (
                <p className="max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                  {field(heroMedia, "subhead")}
                </p>
              ) : null}
              <div className="site-hero__actions mt-8 flex flex-wrap items-center gap-4">
                {primaryCtaLabel && primaryCta?.href ? (
                  <Button asChild
                      size="lg"
                      className="site-button-primary px-6"
                    >
                      <a href={primaryCta.href}>{primaryCtaLabel}<ArrowRight className="ml-2 h-5 w-5" /></a>
                    </Button>
                ) : null}
                {secondaryCtaLabel && secondaryCta?.href ? (
                  <Button asChild
                      variant="secondary"
                      size="lg"
                      className="site-button-light px-6"
                    >
                      <a href={secondaryCta.href}>{secondaryCtaLabel}</a>
                    </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        {videoUrl ? (
          <button
            type="button"
            className="site-hero__video-control"
            aria-label={t(isVideoPaused ? "Play background video" : "Pause background video")}
            onClick={() => {
              const video = videoRef.current;
              if (!video) return;
              if (video.paused) video.play().catch(() => setIsVideoPaused(true));
              else video.pause();
            }}
          >
            {isVideoPaused ? <Play size={16} /> : <Pause size={16} />}
          </button>
        ) : null}
      </div>
    </section>
  );
};

function downloadMockPdf(name) {
  if (!SHOULD_USE_MOCK_DATA) return;
  const content = `%PDF-1.4\n% Mock specs for ${name}\n1 0 obj <<>> endobj\ntrailer <<>>\n%%EOF`;
  const blob = new Blob([content], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name.replace(/\s+/g, "_")}_Specs.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const Products = ({ products }) => {
  const { t, field } = useI18n();
  const items = Array.isArray(products) ? products.filter(Boolean) : [];

  if (!items.length) return null;

  return (
    <section id="products" className="site-products relative bg-[#f5f6f8] py-20 md:py-24">
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="mt-4 font-heading text-3xl text-slate-900 md:text-5xl">
              {t("Holom & Turix VTOL systems")}
            </h2>
            <p className="mt-4 max-w-xl text-sm text-slate-600 md:text-base">
              {t("Choose the configuration engineered for your payload, mission radius and deployment tempo.")}
            </p>
          </div>
          <Button asChild className="site-button-primary self-start px-6 md:self-end">
            <a href="#contact">{t("Contact sales")}</a>
          </Button>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {items.map((p) => {
            const leftBadges = p.badges?.filter((badge) => badge.position !== "right");
            const rightBadges = p.badges?.filter((badge) => badge.position === "right");
            const productImage = getImageUrl(p.image);
            const specUrl = getMediaUrl(p.specFile || p.specUrl || p.specs);
            const localizedBullets = field(p, "bullets");
            const bullets = Array.isArray(localizedBullets) ? localizedBullets : [];
            const localizedBadgeLabels = field(p, "badgeLabels");
            const renderBadge = (badge) => (
              <span
                key={badge.label}
                className={`inline-flex items-center rounded px-3 py-1.5 text-xs font-semibold ${
                  badge.variant === "alt" ? "badge-variant-alt" : "badge-variant-primary"
                }`}
              >
                {localizedBadgeLabels?.[p.badges.indexOf(badge)] || field(badge, "label")}
              </span>
            );

            return (
              <Card
                key={p._id || p.id || p.name}
                className="site-product group relative h-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-none"
              >
                <div
                  className="relative flex h-full flex-col"
                >
                  <div className="relative">
                    {(leftBadges?.length || rightBadges?.length) && (
                      <div className="absolute left-8 right-8 top-8 z-10 flex flex-wrap items-center justify-between gap-3 text-[11px]">
                        <div className="flex flex-wrap gap-3">
                          {leftBadges?.map(renderBadge)}
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {rightBadges?.map(renderBadge)}
                        </div>
                      </div>
                    )}
                    <div className="site-product__image relative aspect-[16/10] w-full overflow-hidden bg-slate-100">
                      {productImage ? (
                        <img
                          src={productImage}
                          alt={p.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="site-product__details flex flex-1 flex-col gap-6 p-6 md:p-8">
                    <div className="flex flex-col gap-4">
                      <h3 className="site-product__name font-heading text-3xl font-medium text-slate-900">
                        {field(p, "name")}
                      </h3>
                      {p.tagline ? (
                        <p className="text-base text-slate-600 md:text-lg">{field(p, "tagline")}</p>
                      ) : null}
                    </div>
                    <ul className="grid flex-1 grid-cols-1 content-start text-sm text-slate-600">
                      {bullets.map((b, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-3 border-t border-slate-200 py-3 leading-relaxed"
                        >
                          <span
                            className="bg-accent mt-2 h-1 w-1 flex-shrink-0 rounded-full"
                          />
                          <span className="text-slate-700">{b}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto flex flex-wrap items-center gap-3">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          if (specUrl) {
                            window.open(specUrl, "_blank", "noopener,noreferrer");
                          } else if (SHOULD_USE_MOCK_DATA) {
                            downloadMockPdf(p.name);
                          }
                        }}
                        className="site-button-primary px-5"
                        disabled={!specUrl && !SHOULD_USE_MOCK_DATA}
                      >
                        {t("Download specs")}
                        <Download className="ml-2 h-4 w-4" />
                      </Button>
                      <Button asChild variant="ghost" className="site-button-outline px-5">
                        <a href="#contact">{t("Talk to us")}</a>
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Features = ({ features }) => {
  const { t, field } = useI18n();
  const items = Array.isArray(features) ? features.filter(Boolean) : [];

  if (!items.length) return null;

  return (
    <section
      id="features"
      className="relative overflow-hidden bg-[#0a0b10] py-20 text-white md:py-24"
    >
      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="max-w-xl">
          <h2 className="mt-4 font-heading text-3xl leading-tight md:text-5xl">
            {t("Technology, simplified")}
          </h2>
          <p className="mt-5 text-sm text-white/70 md:text-base">
            {t("Built from the ground up for endurance, maintainability and mission success.")}
          </p>
          <p className="mt-6 text-sm text-white/60 md:text-base">
          {t("A standards-based avionics stack, modular payload architecture, and clear operating procedures let your team focus on mission results—not babysitting aircraft.")}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {items.map((f) => {
            const Icon = icons[f.icon];
            return (
              <div
                key={f.title}
                className="relative border-t border-white/20 py-6"
              >
                <div className="relative flex flex-col gap-4">
                  {Icon && (
                    <div
                      className="inline-flex text-white/80"
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-white">{field(f, "title")}</h3>
                  <p className="text-sm text-white/70">{field(f, "desc")}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const UseCases = ({ useCases = [] }) => {
  const { t, field } = useI18n();
  const cases = Array.isArray(useCases) ? useCases.filter(Boolean) : [];
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!cases.length) {
      if (activeIndex !== 0) setActiveIndex(0);
    } else if (activeIndex >= cases.length) {
      setActiveIndex(0);
    }
  }, [cases.length, activeIndex]);

  if (!cases.length) return null;

  const activeCase = cases[activeIndex] || {};
  const headline = field(activeCase, "headline") || field(activeCase, "title");
  const subheadline =
    field(activeCase, "subheadline") ||
    field(activeCase, "subtitle") ||
    field(activeCase, "description");

  const goTo = (index) => {
    setActiveIndex((index + cases.length) % cases.length);
  };

  return (
    <section id="use-cases" className="relative isolate overflow-hidden bg-[#05060d] text-white">
      <div className="relative min-h-[560px] w-full">
        <div className="absolute inset-0" aria-hidden>
          {cases.map((item, index) => {
            const imageUrl = getImageUrl(item.image);
            return (
              <div
                key={item._id || item.id || index}
                className={`absolute inset-0 transition-opacity duration-500 ease-out ${
                  index === activeIndex ? "opacity-100" : "opacity-0"
                }`}
                aria-hidden={index !== activeIndex}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-full w-full bg-[#05060d]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80" />
              </div>
            );
          })}
        </div>

        <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col items-start justify-center gap-6 px-6 py-24">
          <h2 className="font-heading text-3xl leading-tight md:text-5xl">
            {headline}
          </h2>
          {subheadline ? (
            <p className="max-w-2xl text-base text-white/80 md:text-lg">{subheadline}</p>
          ) : null}
        </div>

        {cases.length > 1 && (
          <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-5 px-6 pb-12">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                className="site-button-light h-11 w-11 p-0"
                onClick={() => goTo(activeIndex - 1)}
                aria-label={t("Previous use case")}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                className="site-button-light h-11 w-11 p-0"
                onClick={() => goTo(activeIndex + 1)}
                aria-label={t("Next use case")}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {cases.map((item, index) => (
                <button
                  key={item._id || item.id || `dot-${index}`}
                  type="button"
                  onClick={() => goTo(index)}
                  className={`relative h-11 w-9 bg-transparent after:absolute after:inset-x-0 after:top-5 after:h-0.5 transition ${
                    index === activeIndex ? "after:bg-white" : "after:bg-white/40 hover:after:bg-white/70"
                  }`}
                  aria-label={`${t("Go to use case")} ${index + 1}: ${field(item, "headline") || field(item, "title")}`}
                  aria-pressed={index === activeIndex}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const Team = ({ team }) => {
  const { t, field } = useI18n();
  const sortedTeam = useMemo(() => {
    if (!Array.isArray(team)) return [];
    return [...team].sort((a, b) => {
      const orderA = typeof a?.order === "number" ? a.order : Number.POSITIVE_INFINITY;
      const orderB = typeof b?.order === "number" ? b.order : Number.POSITIVE_INFINITY;
      if (orderA !== orderB) return orderA - orderB;
      const nameA = a?.name?.toLowerCase?.() || "";
      const nameB = b?.name?.toLowerCase?.() || "";
      return nameA.localeCompare(nameB);
    });
  }, [team]);

  if (!sortedTeam.length) return null;

  return (
    <section id="team" className="relative bg-white py-24">
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="flex flex-col gap-5">
          <h2 className="font-heading text-3xl text-slate-900 md:text-5xl">
            {t("Aerospace, autonomy & manufacturing experts")}
          </h2>
          <p className="max-w-2xl text-sm text-slate-600 md:text-base">
            {t("The leadership guiding Holom and Turix brings experience from flight testing, field operations and enterprise deployments.")}
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {sortedTeam.map((m) => {
            const photoUrl = getImageUrl(m.photo || m.avatar || m.image);
            const initials =
              m.initials ||
              (m.name
                ? m.name
                    .split(/\s+/)
                    .map((part) => part[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()
                : "");

            return (
              <Card
                key={m.name}
                className="relative rounded-none border-0 border-t border-slate-200 bg-transparent shadow-none"
              >
                <CardContent className="px-0 pb-0 pt-6">
                  <div className="flex flex-col items-start gap-4">
                    <Avatar className="h-20 w-20 rounded-xl">
                      {photoUrl ? (
                        <AvatarImage src={photoUrl} alt={m.name} className="object-cover" />
                      ) : null}
                      <AvatarFallback className="bg-slate-100 text-slate-700">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="text-base font-semibold text-slate-900">{m.name}</div>
                      <div className="mt-1 text-sm leading-relaxed text-slate-600">{field(m, "role")}</div>
                    </div>
                  </div>
                  <p className="mt-5 text-sm text-slate-600">{field(m, "bio")}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Partners = ({ partners }) => {
  const { t } = useI18n();
  const list = Array.isArray(partners) ? partners.filter(Boolean) : [];

  if (!list.length) return null;

  return (
    <section id="partners" className="relative border-y border-slate-200 bg-[#f5f6f8] py-16">
    <div className="relative mx-auto max-w-7xl px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="mt-3 font-heading text-2xl text-slate-900 md:text-4xl">
            {t("Trusted across industries")}
          </h2>
        </div>
        <p className="text-sm text-slate-600 md:max-w-md">
          {t("Collaborative programs with infrastructure owners, public safety teams and advanced manufacturing leaders.")}
        </p>
      </div>
      <div className="mt-12 grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-5">
        {list.map((p) => {
          const logoUrl = getImageUrl(p.logo) || getImageUrl(p.logoUrl) || (typeof p.logoUrl === "string" ? p.logoUrl : "");
          return (
            <div
              key={p.name}
              className="flex h-24 min-w-0 items-center justify-center px-4 text-sm font-medium text-slate-600"
            >
              {logoUrl ? (
                <img src={logoUrl} alt={`${p.name}`} className="max-h-12 max-w-full object-contain" loading="lazy" />
              ) : (
                <span>{p.name}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

const Blog = ({ blogPosts }) => {
  const { language, t, field } = useI18n();
  const posts = useMemo(() => (Array.isArray(blogPosts) ? blogPosts.filter(Boolean) : []), [blogPosts]);
  const [active, setActive] = useState(null);
  const post = useMemo(() => posts.find((b) => b.slug === active) || null, [active, posts]);
  const portableComponents = useMemo(
    () => (sanityClient ? createPortableTextComponents(sanityClient) : {}),
    []
  );

  if (!posts.length) return null;

  const getPreview = (b) =>
    getImageUrl(b.previewImage) ||
    getImageUrl(b.image) ||
    getImageUrl(b.images) ||
    (typeof b.previewImage === "string" ? b.previewImage : "");
  const getExcerpt = (b) => {
    const localizedExcerpt = field(b, "excerpt");
    if (localizedExcerpt) return localizedExcerpt;
    const localizedContent = field(b, "content");
    const text = Array.isArray(localizedContent)
      ? localizedContent
          .map((blk) => blk.children?.map((c) => c.text).join(" "))
          .join(" ")
      : "";
    return text.split(/\s+/).slice(0, 50).join(" ");
  };

  const shareUrl =
    typeof window !== "undefined" && post ? `${window.location.origin}/blog/${post.slug}` : "";
  const shareTitle = post?.title || "";

  const featuredPost = posts[0];
  const featuredPreview = featuredPost ? getPreview(featuredPost) : "";
  const secondaryPosts = posts.slice(1, 4);

  return (
    <section id="blog" className="bg-white py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-heading text-3xl text-slate-900 md:text-4xl">{t("From the blog")}</h2>
          </div>
          <a href="#contact" className="text-sm text-slate-600 hover:text-slate-900">
            {t("Join the update list")}
          </a>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          {featuredPost && (
            <Card className="group relative overflow-hidden rounded-none border-0 bg-white shadow-none">
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-xl">
                {featuredPreview ? (
                  <img
                    src={featuredPreview}
                    alt={featuredPost.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : null}
              </div>
              <div className="pt-6 text-slate-900">
                  <p className="text-xs font-medium text-slate-600">
                    {featuredPost._postType === "press"
                      ? `${t("In the press")} • ${featuredPost.sourceName}`
                      : t("Featured")}
                  </p>
                  <h3 className="mt-3 text-2xl font-semibold leading-snug">{field(featuredPost, "title")}</h3>
                  <p className="mt-3 text-sm text-slate-600">
                    {featuredPost._postType === "press"
                      ? featuredPost.sourceName
                      : featuredPost.author}{" "}
                    • {new Date(featuredPost.date).toLocaleDateString(language)}
                  </p>
                  {featuredPost._postType === "press" ? (
                    <Button
                      asChild
                      className="site-button-outline mt-5"
                    >
                      <a
                        href={featuredPost.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {language === "es" ? "Leer en" : "Read on"} {featuredPost.sourceName}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setActive(featuredPost.slug)}
                      className="site-button-outline mt-5"
                    >
                      {t("Read post")}
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
              </div>
              <CardContent className="grid gap-4 px-0 pb-0 pt-5">
                <p className="text-sm text-slate-600 md:text-base">{getExcerpt(featuredPost)}</p>
                {featuredPost.hashtags && (
                  <div className="flex flex-wrap gap-3 text-xs leading-relaxed text-slate-600">
                    {featuredPost.hashtags.map((tag) => (
                      <span key={tag}>#{tag.replace(/^#+/, "")}</span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6">
            {secondaryPosts.map((b) => {
              const preview = getPreview(b);
              const isPress = b._postType === "press";
              const cardKey = b._id || b.id || b.slug || b.sourceUrl || b.title;
              return (
                <Card key={cardKey} className="group flex flex-col overflow-hidden rounded-none border-0 bg-white shadow-none">
                  <div className="relative aspect-[16/10] overflow-hidden rounded-xl">
                    {preview ? (
                      <img
                        src={preview}
                        alt={b.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : null}
                    {isPress && (
                      <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700 shadow-sm">
                        <ExternalLink className="h-3 w-3" />
                        {b.sourceName || t("External")}
                      </span>
                    )}
                  </div>
                  <CardContent className="flex flex-col gap-4 px-0 pb-0 pt-6">
                    <div>
                      <h3 className="text-2xl font-semibold leading-snug text-slate-900">{field(b, "title")}</h3>
                      <p className="mt-3 text-xs text-slate-600">
                        {isPress ? b.sourceName : b.author} • {new Date(b.date).toLocaleDateString(language)}
                      </p>
                      <p className="mt-3 text-sm text-slate-600 line-clamp-3">{getExcerpt(b)}</p>
                    </div>
                    {b.hashtags && (
                      <div className="flex flex-wrap gap-2 text-xs leading-relaxed text-slate-600">
                        {b.hashtags.map((tag) => (
                          <span key={tag}>#{tag.replace(/^#+/, "")}</span>
                        ))}
                      </div>
                    )}
                    {isPress ? (
                      <Button
                        asChild
                        className="site-button-outline mt-2 w-fit"
                      >
                        <a href={b.sourceUrl} target="_blank" rel="noopener noreferrer">
                          {language === "es" ? "Leer en" : "Read on"} {b.sourceName}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setActive(b.slug)}
                        className="site-button-outline mt-2 w-fit"
                      >
                        {t("Read post")}
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="site-article max-h-[90vh] overflow-y-auto border border-slate-200 bg-white sm:max-w-4xl">
          {post && (
            <div className="text-slate-700">
              <DialogTitle className="mb-4 pr-6 text-2xl font-semibold leading-snug text-slate-900">{field(post, "title")}</DialogTitle>
              <img
                src={getPreview(post)}
                alt={field(post, "title")}
                className="aspect-video w-full rounded-xl object-cover"
              />
              <DialogDescription className="mt-4 text-sm text-slate-600">
                {post.author} • {new Date(post.date).toLocaleDateString(language)}
                {post.lastUpdate && !Number.isNaN(Date.parse(post.lastUpdate)) ? ` (${t("updated")} ${new Date(post.lastUpdate).toLocaleDateString(language)})` : ""}
              </DialogDescription>
              <div className="prose prose-slate mt-6 max-w-none">
                <PortableText value={field(post, "content")} components={portableComponents} />
              </div>
              {post.hashtags && post.hashtags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2 text-xs leading-relaxed text-slate-600">
                  {post.hashtags.map((tag) => (
                    <span key={tag}>#{tag.replace(/^#+/, "")}</span>
                  ))}
                </div>
              )}
              {post.images && post.images.length > 1 && (
                <div className="mt-6 grid grid-cols-2 gap-2">
                  {(post.previewImage ? post.images : post.images.slice(1)).map((img, idx) => (
                    <img
                      key={idx}
                      src={getImageUrl(img)}
                      alt={`${field(post, "title")} — ${idx + 1}`}
                      className="rounded-xl border border-slate-200"
                    />
                  ))}
                </div>
              )}
              <div className="mt-6 flex items-center gap-3 text-slate-600">
                <span className="text-sm">{t("Share:")}</span>
                <WhatsappShareButton aria-label={t("Share:") + " WhatsApp"} url={shareUrl} title={shareTitle}>
                  <Whatsapp className="h-5 w-5" />
                </WhatsappShareButton>
                <EmailShareButton aria-label={t("Share:") + " Email"} url={shareUrl} subject={shareTitle} body={shareUrl}>
                  <Mail className="h-5 w-5" />
                </EmailShareButton>
                <FacebookShareButton aria-label={t("Share:") + " Facebook"} url={shareUrl} quote={shareTitle}>
                  <Facebook className="h-5 w-5" />
                </FacebookShareButton>
                <LinkedinShareButton aria-label={t("Share:") + " LinkedIn"} url={shareUrl} title={shareTitle}>
                  <Linkedin className="h-5 w-5" />
                </LinkedinShareButton>
                <TwitterShareButton aria-label={t("Share:") + " X"} url={shareUrl} title={shareTitle}>
                  <Twitter className="h-5 w-5" />
                </TwitterShareButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const FAQ = ({ faqs }) => {
  const { t, field } = useI18n();
  return Array.isArray(faqs) && faqs.length ? (
    <section id="faq" className="relative overflow-hidden bg-[#0a0b10] py-20 text-white md:py-24">
    <div className="relative mx-auto max-w-4xl px-6">
      <div className="text-center">
          <h2 className="mt-4 font-heading text-3xl md:text-5xl">{t("Frequently asked questions")}</h2>
        <p className="mt-4 text-sm text-white/70 md:text-base">
          {t("Answers to the topics that usually kick off discovery calls.")}
        </p>
      </div>
      <Accordion type="single" collapsible className="mt-10 border-t border-white/20">
        {faqs.map((f, idx) => (
          <AccordionItem
            key={f._id || f.q || idx}
            value={`item-${idx}`}
            className="border-b border-white/10 last:border-none"
          >
            <AccordionTrigger className="py-6 text-left text-base font-medium text-white hover:no-underline">
              {field(f, "q")}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-white/70">{field(f, "a")}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
  ) : null;
};

const Contact = () => {
  const { t } = useI18n();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formMeta, setFormMeta] = useState(() => ({ timestamp: Date.now() }));

  async function onSubmit(e) {
    e.preventDefault();
    const formEl = e.currentTarget;
    if (loading) return;
    setLoading(true);

    const formData = new FormData(formEl);
    const timestampRaw = Number(formData.get("formTimestamp"));
    const payload = {
      name: formData.get("name")?.toString().trim() || "",
      company: formData.get("company")?.toString().trim() || undefined,
      email: formData.get("email")?.toString().trim() || "",
      message: formData.get("message")?.toString().trim() || "",
      formTimestamp: Number.isFinite(timestampRaw) ? timestampRaw : formMeta.timestamp,
      honeypot: formData.get("website")?.toString().trim() || undefined,
    };

    if (payload.honeypot) {
      // Honeypot filled: treat as success without processing.
      formEl.reset();
      setFormMeta({ timestamp: Date.now() });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorDescription = t("We couldn't send your message. Please try again in a moment.");
        try {
          const data = await response.json();
          if (data?.errors) {
            const firstError = Object.values(data.errors)
              .flat()
              .find(Boolean);
            if (firstError) {
              errorDescription = firstError;
            }
          } else if (data?.message) {
            errorDescription = data.message;
          }
        } catch (err) {
          console.error("Failed to parse error response", err);
        }

        toast({
          title: t("Submission failed"),
          description: errorDescription,
        });
        return;
      }

      toast({
        title: t("Thanks!"),
        description: t("Your message was received. We'll contact you shortly."),
      });

      try {
        formEl.reset();
      } catch (err) {
        console.error("Failed to reset form", err);
      }
      setFormMeta({ timestamp: Date.now() });
    } catch (error) {
      console.error("Contact form submission failed", error);
      toast({
        title: t("Submission failed"),
        description: t("We couldn't send your message. Please try again later."),
      });
    } finally {
      setLoading(false);
      if (typeof window !== "undefined" && window.location.hash !== "#contact") {
        window.location.hash = "contact";
      }
    }
  }

  return (
    <section id="contact" className="relative overflow-hidden bg-[#f5f6f8] py-20 md:py-24">
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid items-start gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="max-w-xl">
            <h2 className="mt-4 font-heading text-3xl text-slate-900 md:text-5xl">{t("Contact sales")}</h2>
            <p className="mt-4 text-sm text-slate-600 md:text-base">
              {t("Tell us about your mission and timeline. We’ll propose the right aircraft, payloads and support package.")}
            </p>
            <div className="mt-8 flex items-center gap-4 text-sm text-slate-700">
              <Mail className="h-5 w-5" />
              <a href="mailto:hello@imago.aero" className="font-medium text-slate-900 hover:text-slate-600">
                hello@imago.aero
              </a>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 text-xs leading-relaxed text-slate-600">
              <div className="border-t border-slate-300 py-4">
                <p className="text-slate-900">{t("Deployment support")}</p>
                <p className="mt-2 text-[11px] text-slate-500">{t("Training & field services")}</p>
              </div>
              <div className="border-t border-slate-300 py-4">
                <p className="text-slate-900">{t("Customization")}</p>
                <p className="mt-2 text-[11px] text-slate-500">{t("Payload & data workflows")}</p>
              </div>
            </div>
          </div>

          <Card className="relative rounded-xl border border-slate-200 bg-white shadow-none">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={onSubmit} className="grid grid-cols-1 gap-5" aria-busy={loading}>
                <input type="hidden" name="formTimestamp" value={formMeta.timestamp} />
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="website">{t("Website")}</label>
                  <input
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-name" className="text-sm font-medium text-slate-700">{t("Name")}</label>
                    <Input id="contact-name" autoComplete="name" name="name" required className="site-field rounded-md border-slate-300 bg-white text-base text-slate-900 shadow-none" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="contact-company" className="text-sm font-medium text-slate-700">{t("Company")}</label>
                    <Input id="contact-company" autoComplete="organization" name="company" className="site-field rounded-md border-slate-300 bg-white text-base text-slate-900 shadow-none" />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-email" className="text-sm font-medium text-slate-700">{t("Email")}</label>
                  <Input id="contact-email" autoComplete="email" name="email" type="email" required className="site-field rounded-md border-slate-300 bg-white text-base text-slate-900 shadow-none" />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-message" className="text-sm font-medium text-slate-700">{t("Mission outline")}</label>
                  <Textarea id="contact-message" autoComplete="off" name="message" rows={5} required className="site-field rounded-md border-slate-300 bg-white text-base text-slate-900 shadow-none" />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="site-button-primary mt-2 justify-self-start px-6"
                >
                  {t(loading ? "Sending..." : "Send message")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ brand }) => {
  const { t } = useI18n();
  const logoSrc = getImageUrl(brand?.logo) || brand?.logoUrl || "";

  return (
    <footer className="relative overflow-hidden border-t border-white/15 bg-[#0a0b10] py-14 text-white">
      <div className="relative mx-auto flex max-w-7xl flex-col gap-10 px-6 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            {logoSrc ? (
              <img src={logoSrc} alt="IMAGO Aerospace" className="h-8 w-auto max-w-[150px] object-contain" />
            ) : (
              <div className="text-base font-semibold uppercase tracking-[0.35em]">
                {brand?.name || "IMAGO"}
              </div>
            )}
            <span className="text-sm text-white/70">© {new Date().getFullYear()} {brand?.name}</span>
          </div>
          <p className="max-w-sm text-sm text-white/60">
            {t("Next-generation VTOL systems engineered and manufactured in Latin America.")}
          </p>
        </div>
        <div className="flex max-w-xl flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/75">
          <a href="#products" className="transition hover:text-white">
            {t("Products")}
          </a>
          <a href="#features" className="transition hover:text-white">
            {t("Technology")}
          </a>
          <a href="#team" className="transition hover:text-white">
            {t("Team")}
          </a>
          <a href="#blog" className="transition hover:text-white">
            {t("Blog")}
          </a>
          <a href="#contact" className="transition hover:text-white">
            {t("Contact")}
          </a>
          <a
            href="https://www.linkedin.com/company/imago-aerospace"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 transition hover:text-white"
          >
            <Linkedin className="h-4 w-4" /> LinkedIn
          </a>
          <a href="/privacy" className="transition hover:text-white">
            {t("Privacy & Cookies")}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default function LandingPage() {
  const { t } = useI18n();
  const [content, setContent] = useState(
    SHOULD_USE_MOCK_DATA
      ? {
          brand: mockBrand,
          heroMedia: mockHeroMedia,
          products: mockProducts,
          features: mockFeatures,
          useCases: mockUseCases,
          team: mockTeam,
          partners: mockPartners,
          blogPosts: mockBlogPosts,
          faqs: mockFaqs,
        }
      : {
          brand: null,
          heroMedia: null,
          products: [],
          features: [],
          useCases: [],
          team: [],
          partners: [],
          blogPosts: [],
          faqs: [],
        }
  );
  const [isLoading, setIsLoading] = useState(!SHOULD_USE_MOCK_DATA);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      if (!sanityClient) {
        if (!SHOULD_USE_MOCK_DATA && isMounted) {
          setIsLoading(false);
        }
        return;
      }
      const [
        brandData,
        heroData,
        productsData,
        featuresData,
        useCasesData,
        teamData,
        partnersData,
        blogPostsData,
        pressArticlesData,
        faqsData,
      ] = await Promise.all([
        fetchSanity('*[_type=="brand"][0]'),
        fetchSanity('*[_type=="hero"][0]'),
        fetchSanity('*[_type=="product"]'),
        fetchSanity('*[_type=="feature"]'),
        fetchSanity(
          '*[_type=="useCase"]|order(coalesce(order, 1e6) asc, _createdAt asc){_id, title, headline, subheadline, order, image, translations}'
        ),
        fetchSanity('*[_type=="teamMember"]|order(coalesce(order, 1e6) asc, _createdAt asc)'),
        fetchSanity('*[_type=="partner"]{name, "logo": logo.asset->url}'),
        fetchSanity('*[_type=="blogPost"]{..., "previewImage": previewImage.asset->url, "images": images[].asset->url, "slug": slug.current}'),
        fetchSanity('*[_type=="pressArticle"]{_id, title, sourceName, sourceUrl, date, "previewImage": previewImage.asset->url, excerpt, hashtags, translations}'),
        fetchSanity('*[_type=="faq"]'),
      ])

      const mergedPosts =
        blogPostsData || pressArticlesData
          ? [
              ...((blogPostsData || []).map((p) => ({...p, _postType: 'blog'}))),
              ...((pressArticlesData || []).map((p) => ({...p, _postType: 'press'}))),
            ].sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0))
          : null

      const normalizedUseCases = Array.isArray(useCasesData)
        ? useCasesData.map((item, index) => ({
            ...item,
            id: item._id || item.id || `use-case-${index + 1}`,
            headline: item.headline || item.title,
            backgroundImage: item.backgroundImage || item.image,
            media: item.media || item.image,
          }))
        : null

      if (!isMounted) return;

      setContent(prev => ({
        ...prev,
        ...(brandData && { brand: brandData }),
        ...(heroData && { heroMedia: heroData }),
        ...(productsData && { products: productsData }),
        ...(featuresData && { features: featuresData }),
        ...(normalizedUseCases && { useCases: normalizedUseCases }),
        ...(teamData && { team: teamData }),
        ...(partnersData && { partners: partnersData }),
        ...(mergedPosts && { blogPosts: mergedPosts }),
        ...(faqsData && { faqs: faqsData }),
      }))
    }
    load().finally(() => {
      if (!SHOULD_USE_MOCK_DATA && isMounted) {
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    applyBrandTheme(content.brand)
  }, [content.brand])

  if (!SHOULD_USE_MOCK_DATA && isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#05060d] text-white">
        <span>{t("Loading content...")}</span>
      </main>
    );
  }

  return (
    <main id="main-content" className="imago-site bg-[#05060d] text-slate-900">
      <a href="#products" className="site-skip-link">{t("Explore products")}</a>
      <Header brand={content.brand} />
      <Hero heroMedia={content.heroMedia} />
      <Products products={content.products} />
      <Features features={content.features} />
      <UseCases useCases={content.useCases} />
      <Team team={content.team} />
      <Partners partners={content.partners} />
      <Blog blogPosts={content.blogPosts} />
      <FAQ faqs={content.faqs} />
      <Contact />
      <Footer brand={content.brand} />
    </main>
  );
}
